import { Pagination, useMediaQuery } from "@mui/material";
import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import { AdminIcon, DownloadIcon, StopIcon, UpdateIcon } from "../../assets/img/icons";
import Button from "../../components/Button";
import ConfirmModal from "../../components/ConfirmModal";
import ConnectedLayout from "../../components/ConnectedLayout";
import Header from "../../components/Header";
import Input from "../../components/Input";
import { Select as OldSelect } from "../../components/OldSelect";
import TimeslotsSelect from "../../components/OldSelect/TimeslotsSelect";
import Switch from "../../components/Switch";
import Text from "../../components/Text";
import { AuthContext } from "../../contexts/AuthContext";
import { getAllRoles, updateUserRole } from "../../services/api/admin/role";
import { getPromotionOptions, Promotion } from "../../services/api/admin/promotions";
import { getUsers, getUsersCSV, cacheMemberDetailsUser, mapToMemberEditUser } from "../../services/api/admin/user";
import {
    AdminMembersSortBy,
    MemberEditUser,
} from "../../types/api/admin/user";
import { addFavoriteToUser, removeFavoriteFromUser } from "../../services/api/favorite";
import { updateUserById } from "../../services/api/admin/user";
import { Role } from "../../types/Role";
import { Role as RoleName } from "../../types/enums/Role";
import { UserAdminResult } from "../../types/User";
import ImportBulkModal from "./ImportBulkModal";
import MemberDetails from "./MemberDetails";
import MemberModal from "./MemberModal";
import MembersTable from "./MembersTable";

import DatePicker from "../../components/DatePicker";
import MindsetSelect from "../../components/OldSelect/MindsetSelect";
import Select from "../../components/Select";
import TagsSelect from "../../components/Select/TagsSelect";
import { Mindset } from "../../types/enums/Mindset";
import { getFormattedDateInput } from "../../utils/date";
import { getTraderRoleSortRank, isStaffRole } from "../../utils/graduation";
import { TimeslotCategory } from "../../utils/timeslots";
import styles from "./styles.module.scss";

const Members: FC = () => {
    const { hasPermission } = useContext(AuthContext)!;

    const [loading, setLoading] = useState(true);

    const [users, setUsers] = useState<UserAdminResult[]>([]);
    const [currentUsersPage, setCurrentUsersPage] = useState<UserAdminResult[]>(
        []
    );
    const [page, setPage] = useState(1);
    const [maxPage, setMaxPage] = useState(1);
    const [userByPage, setUserByPage] = useState(10);
    // When we return from a member's details/trades/payouts page, we rely on the
    // pagination values already present in the URL (`page` + `userByPage`).
    // This ref prevents the initial "reset to page 1" behavior from overwriting them.
    const skipPageResetUntilFirstFetchCompleteRef = useRef(false);
    const firstFetchCompletedRef = useRef(false);
    const isMobile = useMediaQuery("(max-width: 1050px)");

    const lastThirtyDays = new Date();
    lastThirtyDays.setDate(lastThirtyDays.getDate() - 30);

    const [searchInput, setSearchInput] = useState("");
    const [debouncedSearchInput, setDebouncedSearchInput] = useState("");
    // Pour stocker le timeout
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
    // Permet d'ignorer les réponses des requêtes obsolètes (évite les race conditions)
    const latestRequestId = useRef(0);
    // Indique que les filtres initiaux (depuis l'URL) ont été appliqués
    const [filtersReady, setFiltersReady] = useState(false);
    type ActivityStatusFilter = "active" | "inactive" | "all";
    const activityStatusOptions: { value: ActivityStatusFilter; label: string }[] = [
        { value: "active", label: "Actifs" },
        { value: "inactive", label: "Inactifs" },
        { value: "all", label: "Tous" },
    ];
    const [roleFilter, setRoleFilter] = useState<string[]>([]);
    const [activityStatus, setActivityStatus] =
        useState<ActivityStatusFilter>("active");
    const [showDisabledAccountsOnly, setShowDisabledAccountsOnly] =
        useState(false);
    const toActivityStatus = (value: string): ActivityStatusFilter =>
        value === "inactive" || value === "all"
            ? value
            : "active";
    const accountEnabledStatus = showDisabledAccountsOnly
        ? "disabled"
        : "enabled";
    const [startDate, setStartDate] = useState(lastThirtyDays);
    const [endDate, setEndDate] = useState(new Date());
    const [timeslot, setTimeslot] = useState<TimeslotCategory | undefined>(
        undefined
    );
    const [mindset, setMindset] = useState<Mindset | undefined>(undefined);
    const [tags, setTags] = useState<string[]>([]);
    const [favoritesOnly, setFavoritesOnly] = useState(false);
    const [promotionFilter, setPromotionFilter] = useState("");
    const [promoOptions, setPromoOptions] = useState<Promotion[]>([]);

    const moneyManagementOptions = {
        "": "-",
        normal: "Normal",
        challenge: "Challenge",
    };
    const [moneyManagement, setMoneyManagement] =
        useState<keyof typeof moneyManagementOptions>("");

    const [sort, setSort] = useState<{
        by: AdminMembersSortBy;
        order: "asc" | "desc";
    }>({
        by: "method",
        order: "desc",
    });

    const [roles, setRoles] = useState<Role[]>([]);
    const roleFilterOptions = [
        ...roles
            .filter((role) => !isStaffRole(role.name))
            .sort(
                (a, b) =>
                    getTraderRoleSortRank(a.name) -
                    getTraderRoleSortRank(b.name)
            )
            .map((role) => ({
                value: role.name,
                label: role.name,
            })),
        ...([RoleName.Formateur, RoleName.Admin, RoleName.SuperAdmin] as const)
            .map((name) => roles.find((role) => role.name === name))
            .filter((role): role is Role => Boolean(role))
            .map((role) => ({
                value: role.name,
                label: role.name,
            })),
    ];

    const promotionFilterOptions = useMemo(
        () => [
            { value: "", label: "Toutes les promos" },
            { value: "none", label: "Sans promo" },
            ...promoOptions.map((p) => ({ value: p.id, label: p.name })),
        ],
        [promoOptions]
    );

    const [memberModalState, setMemberModalState] = useState<boolean>(false);
    const [userToEdit, setUserToEdit] = useState<MemberEditUser | null>(null);

    const [confirmModal, setConfirmModal] = useState<{
        open: boolean;
        user: UserAdminResult | undefined;
        isLoading?: boolean;
    }>({ open: false, user: undefined, isLoading: false });

    const [memberDetailsState, setMemberDetailsState] =
        useState<boolean>(false);
    const [userToView, setUserToView] = useState<UserAdminResult | null>(null);
    const [importBulkModalOpen, setImportBulkModalOpen] = useState(false);

    useEffect(() => {
        getAllRoles().then((loadedRoles) => {
            setRoles(loadedRoles);
        });
        getPromotionOptions()
            .then((rows) => setPromoOptions(rows ?? []))
            .catch(() => {
                /* filtre promo optionnel */
            });
    }, []);

    const fetchUsers = async () => {
        const requestId = ++latestRequestId.current;
        setLoading(true);

        getUsers({
            search: searchInput,
            startDate: getFormattedDateInput(startDate),
            endDate: getFormattedDateInput(endDate),
            sortBy: sort.by,
            sortOrder: sort.order,
            roles: roleFilter.length > 0 ? roleFilter : undefined,
            activityStatus,
            accountEnabledStatus,
            timeslot: timeslot || undefined,
            mindset: mindset || undefined,
            moneyManagement: moneyManagement || undefined,
            tags: tags.length > 0 ? tags : undefined,
            favoritesOnly,
            promotionId: promotionFilter || undefined,
        })
            .then((body) => {
                // Si une requête plus récente a été lancée, on ignore cette réponse
                if (requestId !== latestRequestId.current) return;

                if (!body || body.length === 0) {
                    setUsers([]);
                    setCurrentUsersPage([]);
                    setMaxPage(1);
                    return;
                }

                setPage(1);
                setUsers(body);
                setMaxPage(Math.ceil(body.length / userByPage));
            })
            .catch(() => {
                // Si la requête est obsolète, on ignore aussi les erreurs éventuelles
                if (requestId !== latestRequestId.current) return;
                setUsers([]);
            })
            .finally(() => {
                if (requestId === latestRequestId.current) {
                    setLoading(false);
                }
            });
    };

    // Debounce sur la recherche
    useEffect(() => {
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        debounceTimeout.current = setTimeout(() => {
            setDebouncedSearchInput(searchInput);
        }, 400);
        return () => {
            if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        };
    }, [searchInput]);

    // Read filters from URL on mount
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        let paginationRestoredFromStorage = false;

        // If we came back from a member's details/trades, we can restore the exact
        // pagination snapshot even if the URL hasn't been fully synced yet.
        const rawPagination =
            sessionStorage.getItem("adminMembersReturnPagination");
        if (rawPagination) {
            try {
                const parsed = JSON.parse(rawPagination) as {
                    page?: number;
                    userByPage?: number;
                };

                if (
                    typeof parsed.page === "number" &&
                    !isNaN(parsed.page) &&
                    parsed.page > 0
                ) {
                    setPage(parsed.page);
                }

                if (
                    typeof parsed.userByPage === "number" &&
                    !isNaN(parsed.userByPage) &&
                    parsed.userByPage > 0
                ) {
                    setUserByPage(parsed.userByPage);
                }

                paginationRestoredFromStorage = true;
                skipPageResetUntilFirstFetchCompleteRef.current = true;
                firstFetchCompletedRef.current = false;
            } catch {
                // Ignore corrupted payload
            } finally {
                sessionStorage.removeItem("adminMembersReturnPagination");
            }
        }

        const qSearch = params.get("search");
        if (qSearch !== null) {
            setSearchInput(qSearch);
            // Aligne tout de suite la valeur débouncée pour éviter une requête avec la valeur par défaut
            setDebouncedSearchInput(qSearch);
        }

        const qRoles = params.get("roles");
        if (qRoles) {
            const parsed = qRoles
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);
            if (parsed.length > 0) setRoleFilter(parsed);
        }
        const qActivityStatus = params.get("activityStatus");
        if (
            qActivityStatus === "active" ||
            qActivityStatus === "inactive" ||
            qActivityStatus === "all"
        ) {
            setActivityStatus(qActivityStatus);
        }
        const qShowDisabledAccountsOnly =
            params.get("showDisabledAccountsOnly");
        if (qShowDisabledAccountsOnly === "true") {
            setShowDisabledAccountsOnly(true);
        }
        // Rétrocompat URL avec anciens paramètres proFilter / proOnly / pro6mOnly
        const qProFilter = params.get("proFilter");
        const qMemberFilter = params.get("memberFilter");
        const qPro = params.get("proOnly");
        const qPro6m = params.get("pro6mOnly");
        if (!qRoles && (qProFilter || qMemberFilter || qPro || qPro6m)) {
            const legacy: string[] = [];
            const fromProFilter = (qProFilter ?? "")
                .split(",")
                .map((s) => s.trim());
            // pro6mOnly legacy → Pro (rôle Pro6M retiré)
            if (
                fromProFilter.includes("pro") ||
                fromProFilter.includes("pro6m") ||
                qPro === "true" ||
                qPro6m === "true"
            ) {
                legacy.push("Pro");
            }
            if (legacy.length > 0) setRoleFilter(legacy);
        }

        const qInactifs = params.get("inactifs");
        const qShowInactif = params.get("showInactif");
        const qIsActive = params.get("isActive");
        if (!qActivityStatus) {
            const qAccountStatus = params.get("accountStatus");
            if (qAccountStatus === "active_only") {
                setActivityStatus("active");
            } else if (qAccountStatus === "inactive_only") {
                setActivityStatus("inactive");
            } else if (qAccountStatus === "active_and_inactive") {
                setActivityStatus("all");
            }
            if (qInactifs === "true" || qShowInactif === "true") {
                setActivityStatus("inactive");
            } else if (qIsActive === "true") {
                setActivityStatus("active");
            }
        }

        const qStart = params.get("startDate");
        if (qStart) setStartDate(new Date(qStart));

        const qEnd = params.get("endDate");
        if (qEnd) setEndDate(new Date(qEnd));

        const qTimeslot = params.get("timeslot");
        if (
            qTimeslot === "morning" ||
            qTimeslot === "afternoon" ||
            qTimeslot === "evening"
        ) {
            setTimeslot(qTimeslot);
        }

        const qMindset = params.get("mindset");
        if (qMindset) setMindset(qMindset as Mindset);

        const qMoney = params.get("moneyManagement");
        if (qMoney)
            setMoneyManagement(qMoney as keyof typeof moneyManagementOptions);

        const qUserByPage = params.get("userByPage");
        if (
            !paginationRestoredFromStorage &&
            qUserByPage &&
            !isNaN(Number(qUserByPage))
        ) {
            setUserByPage(Number(qUserByPage));
            skipPageResetUntilFirstFetchCompleteRef.current = true;
            firstFetchCompletedRef.current = false;
        }

        const qPage = params.get("page");
        if (!paginationRestoredFromStorage && qPage && !isNaN(Number(qPage))) {
            setPage(Number(qPage));
            skipPageResetUntilFirstFetchCompleteRef.current = true;
            firstFetchCompletedRef.current = false;
        }

        const qSortBy = params.get("sortBy");
        const qSortOrder = params.get("sortOrder");
        const allowedSortBy = [
            "performance",
            "pseudo",
            "method",
            "average_gain",
            "average_loss",
        ] as const;
        if (
            qSortBy &&
            allowedSortBy.includes(qSortBy as (typeof allowedSortBy)[number]) &&
            (qSortOrder === "asc" || qSortOrder === "desc")
        ) {
            setSort({
                by: qSortBy as AdminMembersSortBy,
                order: qSortOrder as "asc" | "desc",
            });
        }
        const qTags = params.get("tags");
        if (qTags) {
            const tagsFromUrl = qTags.split(",").map((tag) => tag.trim());
            setTags(tagsFromUrl);
        }
        const qFavoritesOnly = params.get("favoritesOnly");
        if (qFavoritesOnly !== null)
            setFavoritesOnly(qFavoritesOnly === "true");

        const qPromotionId = params.get("promotionId");
        if (qPromotionId) setPromotionFilter(qPromotionId);

        // Marque l'initialisation comme terminée pour autoriser la synchro URL et le fetch
        setFiltersReady(true);
    }, []);

    // Sync selected filters to the URL when they change (use replaceState to avoid polluting history)
    useEffect(() => {
        if (!filtersReady) return;

        const params = new URLSearchParams();

        if (debouncedSearchInput) params.set("search", debouncedSearchInput);
        if (roleFilter.length > 0)
            params.set("roles", roleFilter.join(","));
        params.set("activityStatus", activityStatus);
        if (showDisabledAccountsOnly) {
            params.set("showDisabledAccountsOnly", "true");
        }
        if (startDate)
            params.set("startDate", getFormattedDateInput(startDate));
        if (endDate) params.set("endDate", getFormattedDateInput(endDate));
        if (timeslot) params.set("timeslot", String(timeslot));
        if (mindset) params.set("mindset", String(mindset));
        if (moneyManagement)
            params.set("moneyManagement", String(moneyManagement));
        if (userByPage) params.set("userByPage", String(userByPage));
        if (page) params.set("page", String(page));
        if (sort && sort.by) {
            params.set("sortBy", sort.by);
            params.set("sortOrder", sort.order);
        }
        if (tags.length > 0) {
            params.set("tags", tags.join(","));
        }
        if (favoritesOnly) params.set("favoritesOnly", String(favoritesOnly));
        if (promotionFilter) params.set("promotionId", promotionFilter);

        const newRelativePathQuery = `${window.location.pathname}${
            params.toString() ? "?" + params.toString() : ""
        }`;
        window.history.replaceState(null, "", newRelativePathQuery);
    }, [
        debouncedSearchInput,
        roleFilter,
        activityStatus,
        showDisabledAccountsOnly,
        startDate,
        endDate,
        timeslot,
        mindset,
        moneyManagement,
        userByPage,
        page,
        sort,
        filtersReady,
        tags,
        favoritesOnly,
        promotionFilter,
    ]);

    useEffect(() => {
        // Évite un premier fetch avec les valeurs par défaut tant que les filtres URL ne sont pas appliqués
        if (!filtersReady) return;

        const requestId = ++latestRequestId.current;
        setLoading(true);

        getUsers({
            search: debouncedSearchInput,
            startDate: getFormattedDateInput(startDate),
            endDate: getFormattedDateInput(endDate),
            sortBy: sort.by,
            sortOrder: sort.order,
            roles: roleFilter.length > 0 ? roleFilter : undefined,
            activityStatus,
            accountEnabledStatus,
            timeslot: timeslot || undefined,
            mindset: mindset || undefined,
            moneyManagement: moneyManagement || undefined,
            tags: tags.length > 0 ? tags : undefined,
            favoritesOnly,
            promotionId: promotionFilter || undefined,
        })
            .then((body) => {
                // Si une requête plus récente a été lancée, on ignore cette réponse
                if (requestId !== latestRequestId.current) return;

                if (!body || body.length === 0) {
                    setUsers([]);
                    setCurrentUsersPage([]);
                    setMaxPage(1);
                    firstFetchCompletedRef.current = true;
                    return;
                }

                if (!skipPageResetUntilFirstFetchCompleteRef.current) {
                    setPage(1);
                }
                setUsers(body);
                setMaxPage(Math.ceil(body.length / userByPage));
                firstFetchCompletedRef.current = true;
            })
            .catch(() => {
                // Si la requête est obsolète, on ignore aussi les erreurs éventuelles
                if (requestId !== latestRequestId.current) return;
                setUsers([]);
                firstFetchCompletedRef.current = true;
            })
            .finally(() => {
                if (requestId === latestRequestId.current) {
                    setLoading(false);
                }
            });
    }, [
        debouncedSearchInput,
        startDate,
        endDate,
        activityStatus,
        accountEnabledStatus,
        sort.by,
        sort.order,
        roleFilter,
        userByPage,
        confirmModal,
        timeslot,
        mindset,
        moneyManagement,
        filtersReady,
        tags,
        favoritesOnly,
        promotionFilter,
    ]);

    useEffect(() => {
        if (skipPageResetUntilFirstFetchCompleteRef.current) return;
        setPage(1);
    }, [
        searchInput,
        startDate,
        endDate,
        roleFilter,
        activityStatus,
        timeslot,
        mindset,
        moneyManagement,
        sort,
        userByPage,
        tags,
        favoritesOnly,
        promotionFilter,
    ]);

    // Recalcule maxPage à chaque changement de users ou userByPage
    useEffect(() => {
        // If we're restoring pagination and the list is still empty,
        // don't force-reset to page 1 while waiting for the first fetch.
        if (skipPageResetUntilFirstFetchCompleteRef.current) return;

        const newMaxPage = Math.max(1, Math.ceil(users.length / userByPage));
        setMaxPage(newMaxPage);
        if (page > newMaxPage) {
            setPage(1);
        }
    }, [users, userByPage, page]);

    // Découpe les utilisateurs à afficher à chaque changement de page, users ou userByPage
    useEffect(() => {
        const startIndex = (page - 1) * userByPage;
        const endIndex = startIndex + userByPage;
        setCurrentUsersPage(users.slice(startIndex, endIndex));

        // Release the "don't reset to page 1" guard once the first fetch after
        // a restoration has completed.
        if (
            skipPageResetUntilFirstFetchCompleteRef.current &&
            firstFetchCompletedRef.current
        ) {
            skipPageResetUntilFirstFetchCompleteRef.current = false;
        }
    }, [page, users, userByPage, maxPage, setCurrentUsersPage]);

    const onRoleChange = async (userId: string, value: string) => {
        const role = roles.find((role) => role.id === value);

        if (!role) {
            toast.error("Erreur lors de l'attribution du rôle");
            return;
        }

        try {
            await updateUserRole(userId, role.name);
            fetchUsers();
            toast.success("Rôle attribué avec succès");
        } catch (e) {
            toast.error("Erreur lors de la mise à jour du rôle");
        } finally {
            setLoading(false);
        }
    };

    const handleChangeOrder = (column: AdminMembersSortBy) => {
        if (sort.by === column) {
            setSort({ ...sort, order: sort.order === "asc" ? "desc" : "asc" });
            return;
        }

        if (column === "pseudo") {
            setSort({ by: column, order: "asc" });
            return;
        }

        if (column === "average_loss") {
            setSort({ by: column, order: "asc" });
            return;
        }

        setSort({ by: column, order: "desc" });
    };

    // Gère le toggle favori pour un utilisateur
    const handleToggleFavorite = async (isCurrentlyFavorite: boolean, userId: string) => {
        try {
            if (isCurrentlyFavorite) {
                await removeFavoriteFromUser(userId);
            } else {
                await addFavoriteToUser(userId);
            }
            setUsers((prevUsers) =>
                prevUsers.map((user) =>
                    user.id === userId
                        ? { ...user, isFavorite: !isCurrentlyFavorite }
                        : user
                )
            );
        } catch (error) {
            toast.error("Erreur lors de la mise à jour des favoris.");
        }
    };

    const handleOpenModalEditUser = (user: UserAdminResult) => {
        const editUser = mapToMemberEditUser(user);
        if (!editUser) {
            toast.error(
                "Profil incomplet pour ce membre (email ou rôle manquant).",
            );
            return;
        }

        setUserToEdit(editUser);
        setMemberModalState(true);
    };

    const handleDisabledAccountsShortcut = () => {
        setShowDisabledAccountsOnly((prev) => !prev);
    };

    // Confirm modal functions
    const handleConfirmOpen = (user: UserAdminResult) => {
        setConfirmModal({ open: true, user });
    };

    const handleConfirmClose = () => {
        setConfirmModal({ open: false, user: undefined, isLoading: false });
    };

    const handleConfirm = async () => {
        if (!confirmModal.user) return;
        const user = confirmModal.user;

        try {
            setConfirmModal({ ...confirmModal, isLoading: true });
            await handleAccountState(user);
            toast.success(
                `Utilisateur ${
                    user.isActive ? "désactivé" : "activé"
                } avec succès`
            );
        } catch (error) {
            toast.error(
                "Une erreur s'est produite, veuillez réessayer plus tard"
            );
        } finally {
            handleConfirmClose();
        }
    };

    const handleViewUser = (user: UserAdminResult) => {
        cacheMemberDetailsUser(user);
        setMemberDetailsState(true);
        setUserToView(user);
    };

    const handleCloseDetails = () => {
        setMemberDetailsState(false);
        setUserToView(null);
    };

    // Change user account state (activate/deactivate)
    const handleAccountState = async (user: UserAdminResult) => {
        try {
            const { id, firstname, lastname, email, roleId, isActive } = user;

            await updateUserById(id, {
                firstname,
                lastname,
                email,
                roleId,
                isActive: !isActive,
            });
        } catch (error) {
            toast.error("Erreur lors de la mise à jour de l'état du compte");
        }
    };

    const handleExportUsersCSV = async () => {
        try {
            const data = await getUsersCSV({
                search: searchInput,
                startDate: getFormattedDateInput(startDate),
                endDate: getFormattedDateInput(endDate),
                sortBy: sort.by,
                sortOrder: sort.order,
                roles: roleFilter.length > 0 ? roleFilter : undefined,
                activityStatus,
                accountEnabledStatus,
                timeslot: timeslot || undefined,
                mindset: mindset || undefined,
                moneyManagement: moneyManagement || undefined,
                tags: tags.length > 0 ? tags : undefined,
                favoritesOnly,
                promotionId: promotionFilter || undefined,
            });

            // Handle the CSV data (e.g., download it)
            const blob = new Blob([data], { type: "text/csv;charset=utf-8;" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
                "download",
                `membres_${new Date().toISOString().split("T")[0]}.csv`
            );
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error("Erreur lors de l'exportation des utilisateurs");
        }
    };

    return (
        <ConnectedLayout pageContentClassName={styles.page_content}>
            <ConfirmModal
                open={confirmModal.open}
                handleClose={handleConfirmClose}
                handleConfirm={handleConfirm}
                isLoading={confirmModal.isLoading}
            />
            <MemberModal
                open={memberModalState}
                handleClose={() => setMemberModalState(false)}
                user={userToEdit || undefined}
                roles={roles}
                onUserUpdated={fetchUsers}
            />
            <MemberDetails
                open={memberDetailsState}
                handleClose={handleCloseDetails}
                user={userToView || undefined}
            />
            <ImportBulkModal
                open={importBulkModalOpen}
                handleClose={() => setImportBulkModalOpen(false)}
                onCompleted={fetchUsers}
            />
            <Header
                icon={AdminIcon}
                title="Les membres"
                optionalButton={
                    <div className={styles.header_actions}>
                        <DatePicker
                            mode="range"
                            startDate={startDate}
                            setStartDate={setStartDate}
                            endDate={endDate}
                            setEndDate={setEndDate}
                            classNameTrigger={styles.date_picker_trigger}
                        />
                        {hasPermission("admin-members-bulk", "update") && (
                            <Button
                                Icon={UpdateIcon}
                                label="Import CSV"
                                labelOptions={{ noWrap: true }}
                                onClick={() => setImportBulkModalOpen(true)}
                                size="small"
                                variant="outlined"
                            />
                        )}
                        {hasPermission("users-extract", "read") && !isMobile && (
                            <Button
                                Icon={DownloadIcon}
                                label="Exporter"
                                labelOptions={{ noWrap: true }}
                                onClick={handleExportUsersCSV}
                                size="small"
                                variant="outlined"
                            />
                        )}
                    </div>
                }
            />
            <div className={styles.header}>
                <div className={styles.filters}>
                    {isMobile && (
                        <div className={styles.filters_search_date}>
                            <Input
                                value={searchInput}
                                onChanged={setSearchInput}
                                className={styles.filters_input}
                                type="text"
                                placeholder="Rechercher un membre"
                            />
                            <div className={styles.filters_select_container}>
                                <Text variant="body-bold">Rôles</Text>
                                <Select
                                    type="multiple"
                                    options={roleFilterOptions}
                                    value={roleFilter}
                                    onChange={(selected) =>
                                        setRoleFilter(selected as string[])
                                    }
                                    placeholder="-"
                                />
                            </div>
                            <div className={styles.filters_select_container}>
                                <Text variant="body-bold">Comptes</Text>
                                <Select
                                    options={activityStatusOptions}
                                    value={activityStatus}
                                    onChange={(selected) => {
                                        const selectedValue =
                                            typeof selected === "string"
                                                ? selected
                                                : "active";
                                        setActivityStatus(
                                            toActivityStatus(
                                                selectedValue
                                            )
                                        );
                                    }}
                                    placeholder="-"
                                />
                            </div>
                        </div>
                    )}
                    <div className={styles.filters_additional_filters}>
                        <div className={styles.filters_select_row}>
                            {!isMobile && (
                                <Input
                                    value={searchInput}
                                    onChanged={setSearchInput}
                                    className={styles.filters_input}
                                    type="text"
                                    placeholder="Rechercher un membre"
                                />
                            )}
                            <div className={styles.filters_select_container}>
                                <Text variant="body-bold">
                                    Créneaux
                                </Text>
                                <TimeslotsSelect
                                    value={timeslot}
                                    onChanged={(t) => {
                                        setTimeslot(t);
                                    }}
                                />
                            </div>
                            <div className={styles.filters_select_container}>
                                <Text variant="body-bold">État d'esprit</Text>
                                <MindsetSelect
                                    value={mindset}
                                    onChanged={(m) => {
                                        setMindset(m);
                                    }}
                                />
                            </div>
                            <div className={styles.filters_select_container}>
                                <Text variant="body-bold">
                                    {isMobile
                                        ? "Money manag."
                                        : "Money management"}
                                </Text>
                                <OldSelect
                                    value={moneyManagement}
                                    onChanged={(s) => {
                                        setMoneyManagement(
                                            s as keyof typeof moneyManagementOptions
                                        );
                                    }}
                                >
                                    {Object.entries(moneyManagementOptions).map(
                                        ([key, value]) => (
                                            <option key={key} value={key}>
                                                {value}
                                            </option>
                                        )
                                    )}
                                </OldSelect>
                            </div>
                            <div className={styles.filters_select_container}>
                                <Text variant="body-bold">{"Tags"}</Text>
                                <TagsSelect
                                    value={tags}
                                    onChange={(selected) => {
                                        setTags(selected);
                                    }}
                                />
                            </div>
                            <div className={styles.filters_select_container}>
                                <Text variant="body-bold">Promotion</Text>
                                <Select
                                    options={promotionFilterOptions}
                                    value={promotionFilter}
                                    onChange={(selected) => {
                                        setPromotionFilter(
                                            typeof selected === "string"
                                                ? selected
                                                : ""
                                        );
                                    }}
                                    placeholder="-"
                                />
                            </div>
                            {!isMobile && (
                                <div className={styles.filters_toggles}>
                                    <div
                                        className={
                                            styles.filters_select_container
                                        }
                                    >
                                        <Text variant="body-bold">Rôles</Text>
                                        <Select
                                            type="multiple"
                                            options={roleFilterOptions}
                                            value={roleFilter}
                                            onChange={(selected) =>
                                                setRoleFilter(
                                                    selected as string[]
                                                )
                                            }
                                            placeholder="-"
                                        />
                                    </div>
                                    <div
                                        className={
                                            styles.filters_select_container
                                        }
                                    >
                                        <Text variant="body-bold">
                                            Activité
                                        </Text>
                                        <Select
                                            options={activityStatusOptions}
                                            value={activityStatus}
                                            onChange={(selected) => {
                                                const selectedValue =
                                                    typeof selected === "string"
                                                        ? selected
                                                        : "active";
                                                setActivityStatus(
                                                    toActivityStatus(
                                                        selectedValue
                                                    )
                                                );
                                            }}
                                            placeholder="-"
                                        />
                                    </div>
                                    <Switch
                                        checked={favoritesOnly}
                                        onChange={setFavoritesOnly}
                                        label={"Favoris"}
                                    />
                                </div>
                            )}
                            {isMobile && (
                                <Switch
                                    checked={favoritesOnly}
                                    onChange={setFavoritesOnly}
                                    label={"Favoris"}
                                />
                            )}
                        </div>
                    </div>
                </div>
                {!isMobile && hasPermission("admin-members", "update") && (
                    <Button
                        className={styles.filters_disabled_accounts_button}
                        Icon={StopIcon}
                        onClick={handleDisabledAccountsShortcut}
                        size="small"
                        variant="outlined"
                        label={`Voir les comptes ${
                            showDisabledAccountsOnly
                                ? "activés"
                                : "désactivés"
                        }`}
                    />
                )}
            </div>
            <MembersTable
                isLoading={loading}
                users={currentUsersPage}
                roles={roles}
                page={page}
                userByPage={userByPage}
                onRoleChange={onRoleChange}
                handleChangeOrder={handleChangeOrder}
                handleOpenModalEditUser={handleOpenModalEditUser}
                handleConfirmDeleteUser={handleConfirmOpen}
                handleViewUser={handleViewUser}
                sort={sort}
                onToggleFavorite={handleToggleFavorite}
                onBulkCompleted={fetchUsers}
            />
            {users.length > 0 && (
                <div className={styles.members_pagination_container}>
                    <Pagination
                        count={maxPage}
                        onChange={(e, value) => setPage(value)}
                        page={page}
                        siblingCount={0}
                    />
                    <OldSelect
                        value={userByPage.toString()}
                        onChanged={(value) => {
                            setUserByPage(Number(value));
                            setPage(1);
                        }}
                        className={styles.pagination_select}
                    >
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                    </OldSelect>
                </div>
            )}
        </ConnectedLayout>
    );
};

export default Members;
